/**
 * PageCore.ts
 *
 * Core business logic for the payment page.
 * This file extracts all reactive state and methods from Page.vue,
 * making it reusable across different templates.
 *
 * This follows the Composition API pattern, returning reactive state
 * and methods that can be used by any Vue component.
 */

import { ref, reactive, computed, watch, nextTick } from 'vue';
import { format, parseISO } from 'date-fns';
import { decodeLinkParams, type LinkParams } from '$utils/decodeLinkParams';
import logger from '$utils/logger';
import { getFriendlyErrorMessage } from './constants/errorMessages';
import config, { getCurrentEnvironment } from '../config/environment';
import {
    type CustomerInfoSDK,
    type BillingAddressSDK,
    type InvoiceData,
    type SdkDetailsPayload,
    type HotelConfig,
    type BookingSummary,
    type Language,
} from './types';
import { getCountries } from './constants/countries';
import translations, { getTranslation } from './constants/translations';

/**
 * Creates the core page logic
 */
export function createPageCore() {
    // --- Reactive State ---
    let checkoutInstance: any = null;
    const paymentStatus = ref<'idle' | 'processing' | 'success' | 'error'>('idle');
    const isCardFormValid = ref(false);
    const isPoliciesAccepted = ref(false);
    const hotelConfig = ref<HotelConfig | null>(null);
    const hasCurrencyConversion = ref(false);
    const language = ref<Language>('es');
    const requiresInvoice = ref<boolean>(false);
    const isSdkReady = ref(false);
    const confirmationCode = ref<string>('');
    const paymentTimestamp = ref<string>('');
    const copied = ref(false);
    const params = ref<LinkParams | null>(null);
    const pageMode = ref<'hotel' | 'merchant'>('hotel');
    const isLanguageMenuOpen = ref(false);
    const rfcError = ref<string | null>(null);
    const billingCountryDisplay = ref<string>('México');

    const bookingSummary = reactive<BookingSummary>({
        hotelName: '',
        hotelAddress: '',
        checkIn: '',
        checkOut: '',
        rooms: 1,
        guests: 1,
        totalAmount: '0',
        totalCurrency: 'MXN',
        bookingNumber: '',
        urlFailure: '',
        sourceCurrency: '',
        fxRate: 0,
        disableInputs: false,
        items: [],
    });

    const customerInfo = reactive<CustomerInfoSDK>({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
    });

    const billingAddress = reactive<BillingAddressSDK>({
        address: '',
        city: '',
        state: '',
        country: 'MX',
        zip: '',
    });

    const invoiceData = reactive<InvoiceData>({
        rfc: '',
        razon_social: '',
        situacion_fiscal: '',
        uso_factura: '',
        email: '',
        calle: '',
        numero_exterior: '',
        numero_interior: '',
        colonia: '',
        municipio: '',
        estado: '',
        codigo_postal: '',
        pais: 'México',
    });

    // --- Computed Properties ---
    const contactInfo = computed(() => getLocalizedContent(hotelConfig.value?.contactInfo));
    const privacyUrl = computed(() => getLocalizedContent(hotelConfig.value?.urlPrivacy));
    const termsUrl = computed(() => getLocalizedContent(hotelConfig.value?.urlTerms));
    const hasPrivacyOrTermsUrls = computed(() => {
        const privacy = privacyUrl.value?.trim();
        const terms = termsUrl.value?.trim();
        return !!(privacy && privacy.length > 0) || !!(terms && terms.length > 0);
    });
    const canProceedWithPayment = computed(() => {
        if (pageMode.value === 'merchant') {
            return isCardFormValid.value;
        }
        if (hasPrivacyOrTermsUrls.value) {
            return isCardFormValid.value && isPoliciesAccepted.value;
        }
        return isCardFormValid.value;
    });
    const countries = computed(() => getCountries(language.value));

    // --- Watchers ---
    watch(
        () => invoiceData.rfc,
        (newVal) => {
            if (newVal) {
                invoiceData.rfc = newVal.toUpperCase();
                handleRfcValidation();
            }
        }
    );
    watch(
        () => invoiceData.razon_social,
        (newVal) => {
            if (newVal) invoiceData.razon_social = newVal.toUpperCase();
        }
    );
    watch(
        () => invoiceData.situacion_fiscal,
        (newVal) => {
            if (newVal) invoiceData.situacion_fiscal = newVal.toUpperCase();
        }
    );
    watch(
        () => invoiceData.uso_factura,
        (newVal) => {
            if (newVal) invoiceData.uso_factura = newVal.toUpperCase();
        }
    );
    watch(billingCountryDisplay, (newVal) => {
        const countryCode =
            Object.keys(countries.value).find((key) => countries.value[key] === newVal) || 'MX';
        billingAddress.country = countryCode;
    });
    watch(language, () => {
        const currentCountryCode = billingAddress.country;
        const newCountryName = countries.value[currentCountryCode];
        if (newCountryName) {
            billingCountryDisplay.value = newCountryName;
        }
        if (hotelConfig.value) updatePageTitle();
    });

    // --- Helper Functions ---
    const t = (key: keyof typeof translations.en): string => translations[language.value][key];

    const getLocalizedContent = (content?: Record<string, string>): string => {
        if (!content) return '';
        if (content[language.value]) return content[language.value];
        if (content['en']) return content['en'];
        const availableLanguages = Object.keys(content);
        return availableLanguages.length > 0 ? content[availableLanguages[0]] : '';
    };

    const toNumber = (value: unknown): number | null => {
        if (value === undefined || value === null) return null;
        const numericValue = Number(value);
        return Number.isFinite(numericValue) ? numericValue : null;
    };

    const formatCurrency = (amount: number | string, currency: string = 'MXN'): string => {
        if (typeof amount === 'string') amount = parseFloat(amount);
        if (isNaN(amount)) return '$0.00';
        const localeMap = { MXN: 'es-MX', USD: 'en-US', EUR: 'en-US', CAD: 'en-US' };
        const symbolMap = { MXN: '$', USD: 'US$', EUR: '€', CAD: 'C$' };
        const locale = localeMap[currency] || (language.value === 'es' ? 'es-MX' : 'en-US');
        const symbol = symbolMap[currency] || '$';
        const formatter = new Intl.NumberFormat(locale, {
            style: 'decimal',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
        return symbol + formatter.format(amount) + ' ' + currency;
    };

    const formatDualCurrency = (
        sourceAmount: number | string,
        sourceCurrency: string,
        targetAmount: number | string,
        targetCurrency: string
    ): string => {
        const formattedSource = formatCurrency(sourceAmount, sourceCurrency);
        const formattedTarget = formatCurrency(targetAmount, targetCurrency);
        return `${formattedSource} (${formattedTarget})`;
    };

    const formatDate = (dateString: string): string => {
        if (!dateString) return '-';
        let date: Date;
        if (dateString.includes('T') || dateString.includes('Z')) {
            date = parseISO(dateString);
        } else {
            const [year, month, day] = dateString.split('-').map(Number);
            date = new Date(year, month - 1, day);
        }
        const localeMap: Record<Language, string> = {
            es: 'es-ES',
            en: 'en-US',
            fr: 'fr-FR',
            pt: 'pt-BR',
        };
        const locale = localeMap[language.value] || 'es-ES';
        return date.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
    };

    const convertCurrency = (amount: number, exchangeRate: number): number => amount * exchangeRate;

    const computeChargeAmount = (linkParams: LinkParams | null): number | null => {
        if (!linkParams) return null;

        const explicitTotal = toNumber(linkParams.total);
        if (explicitTotal !== null) {
            return explicitTotal;
        }

        const fxRate = toNumber(linkParams.fx_rate);
        const hasConversion = Boolean(
            linkParams.source_currency && fxRate && linkParams.source_currency !== linkParams.currency
        );

        const subtotal = toNumber(linkParams.subtotal);
        const taxes = toNumber(linkParams.taxes);
        const discounts = toNumber(linkParams.discounts);
        const hasBreakdown =
            linkParams.subtotal !== undefined ||
            linkParams.taxes !== undefined ||
            linkParams.discounts !== undefined;

        if (hasBreakdown) {
            let derivedTotal = (subtotal ?? 0) + (taxes ?? 0) - (discounts ?? 0);
            if (!Number.isFinite(derivedTotal)) {
                return null;
            }

            if (hasConversion && fxRate) {
                const sourceAmount = toNumber(linkParams.source_amount);
                if (sourceAmount !== null) {
                    return sourceAmount * fxRate;
                }
                derivedTotal *= fxRate;
            }

            return derivedTotal;
        }

        if (hasConversion && fxRate) {
            const sourceAmount = toNumber(linkParams.source_amount);
            if (sourceAmount !== null) {
                return sourceAmount * fxRate;
            }
        }

        return null;
    };

    // --- DOM Helper Functions ---
    /**
     * Waits for an element to exist in the DOM
     * @param selector CSS selector for the element
     * @param timeout Maximum time to wait in ms (default 10000)
     * @returns Promise that resolves when element is found
     */
    const waitForElement = (selector: string, timeout: number = 10000): Promise<Element> => {
        return new Promise((resolve, reject) => {
            const element = document.querySelector(selector);
            if (element) {
                return resolve(element);
            }

            const observer = new MutationObserver((mutations, obs) => {
                const el = document.querySelector(selector);
                if (el) {
                    obs.disconnect();
                    resolve(el);
                }
            });

            observer.observe(document.body, {
                childList: true,
                subtree: true,
            });

            // Timeout fallback
            setTimeout(() => {
                observer.disconnect();
                reject(new Error(`Element "${selector}" not found after ${timeout}ms`));
            }, timeout);
        });
    };

    // --- Modal/Alert Functions ---
    const showProcessingModal = (_text: string) => {
        paymentStatus.value = 'processing';
    };

    const closeProcessingModal = () => {
        if (paymentStatus.value === 'processing') {
            paymentStatus.value = 'idle';
        }
    };

    const showPaymentErrorModal = async (
        errorMessage: string,
        errorCode?: string | number,
        timestamp?: string
    ) => {
        if (typeof window !== 'undefined' && (window as any).Swal) {
            const confirmButtonTexts = {
                es: 'Aceptar',
                en: 'OK',
                fr: 'OK',
                pt: 'OK',
            };

            let fullMessage = errorMessage;
            if (errorCode && timestamp) {
                fullMessage += `\n\nCódigo: ${errorCode}\n${timestamp}`;
            } else if (errorCode) {
                fullMessage += `\n\nCódigo: ${errorCode}`;
            }

            const result = await (window as any).Swal.fire({
                icon: 'warning',
                title: t('paymentError') || 'Problema con el pago',
                text: fullMessage,
                confirmButtonText: confirmButtonTexts[language.value] || 'OK',
                confirmButtonColor: '#F59E0B',
                iconColor: '#F59E0B',
                allowOutsideClick: false,
                allowEscapeKey: false,
                customClass: {
                    popup: 'swal2-popup-custom',
                    title: 'swal2-title-custom',
                    content: 'swal2-content-custom',
                },
            });

            if (result.isConfirmed && params.value?.url_failure) {
                window.location.href = params.value.url_failure;
            }
        } else {
            paymentStatus.value = 'error';
            if (params.value?.url_failure) {
                setTimeout(() => {
                    window.location.href = params.value.url_failure;
                }, 2000);
            }
        }
    };

    const showPaymentSuccess = () => {
        paymentStatus.value = 'success';
        if (params.value?.url_success) {
            setTimeout(() => {
                window.location.href = params.value.url_success;
            }, 3000);
        }
    };

    const copyConfirmationCode = async () => {
        if (!confirmationCode.value) return;
        try {
            await navigator.clipboard.writeText(confirmationCode.value);
            copied.value = true;
            setTimeout(() => {
                copied.value = false;
            }, 2000);
        } catch (err) {
            logger.error('Error copying confirmation code:', err);
        }
    };

    const checkOnlineStatus = (): boolean => {
        if (!navigator.onLine) {
            const offlineMessage =
                language.value === 'es'
                    ? 'No tienes conexión a internet. Por favor, conecta tu dispositivo a una red e intenta nuevamente.'
                    : 'No internet connection. Please connect your device to a network and try again.';
            showPaymentErrorModal(offlineMessage, 'HP-SYS-912');
            return false;
        }
        return true;
    };

    // --- Invoice Functions ---
    const toggleInvoiceForm = () => {
        requiresInvoice.value = !requiresInvoice.value;
    };

    const validateRFC = (rfc: string): boolean => {
        const rfcPersonaFisica = /^[A-Z\&Ñ]{4}[0-9]{6}[A-Z0-9]{3}$/;
        const rfcPersonaMoral = /^[A-Z\&Ñ]{3}[0-9]{6}[A-Z0-9]{3}$/;
        const cleanRfc = rfc.trim().toUpperCase();
        return rfcPersonaFisica.test(cleanRfc) || rfcPersonaMoral.test(cleanRfc);
    };

    const handleRfcValidation = () => {
        if (invoiceData.rfc.trim() === '') {
            rfcError.value = null;
            return;
        }
        if (!validateRFC(invoiceData.rfc)) {
            rfcError.value = language.value === 'es' ? 'RFC no válido' : 'Invalid RFC';
        } else {
            rfcError.value = null;
        }
    };

    const transformToUppercase = (field: keyof InvoiceData) => {
        if (invoiceData[field]) {
            invoiceData[field] = (invoiceData[field] as string).toUpperCase();
        }
    };

    // --- Language Functions ---
    const setLanguage = (lang: Language) => {
        if (lang !== language.value) {
            logger.info(`Changing language from ${language.value} to ${lang}`);
            language.value = lang;
            localStorage.setItem('selectedLanguage', lang);
            if (checkoutInstance) {
                checkoutInstance.setLanguage(language.value);
            }
            if (hotelConfig.value) {
                updatePageTitle();
            }
        }
    };

    const updatePageTitle = () => {
        if (!hotelConfig.value?.name) return;
        const entityName = hotelConfig.value.name;
        const title =
            language.value === 'es'
                ? `Link de pago ${entityName}`
                : `Payment link ${entityName}`;
        document.title = title;
    };

    // --- Payment Summary Functions ---
    const updatePaymentSummary = async () => {
        if (!params.value) return;

        if (params.value.booking_number) bookingSummary.bookingNumber = params.value.booking_number;

        const resolvedAmount = computeChargeAmount(params.value);
        if (resolvedAmount !== null) {
            bookingSummary.totalAmount = String(resolvedAmount);
        } else if (params.value.total !== undefined) {
            const fallbackTotal = Number(params.value.total);
            if (!Number.isNaN(fallbackTotal)) {
                bookingSummary.totalAmount = String(fallbackTotal);
            }
        }

        if (params.value.currency) bookingSummary.totalCurrency = params.value.currency;

        const fxRate = toNumber(params.value.fx_rate);
        const hasConversion = Boolean(
            params.value.source_currency && fxRate && params.value.source_currency !== params.value.currency
        );
        bookingSummary.sourceCurrency = params.value.source_currency ?? '';
        bookingSummary.fxRate = fxRate ?? 0;
        hasCurrencyConversion.value = hasConversion;

        await nextTick();
        const paymentSummaryEl = document.getElementById('payment-summary');
        if (paymentSummaryEl) {
            paymentSummaryEl.classList.toggle('hidden', !hasConversion);
        }

        if (hasConversion) {
            const sourceAmount =
                toNumber(params.value.source_amount) ??
                (resolvedAmount !== null && fxRate ? resolvedAmount / fxRate : null);
            const targetAmount =
                resolvedAmount ?? (sourceAmount !== null && fxRate ? sourceAmount * fxRate : null);
            const sourceCurrency = params.value.source_currency;
            const targetCurrency = params.value.currency || bookingSummary.totalCurrency;

            if (sourceAmount !== null && targetAmount !== null) {
                const sourceAmountEl = document.getElementById('source-amount');
                const targetAmountEl = document.getElementById('target-amount');
                const exchangeRateEl = document.getElementById('exchange-rate');

                if (sourceAmountEl)
                    sourceAmountEl.textContent = formatCurrency(sourceAmount, sourceCurrency);
                if (targetAmountEl)
                    targetAmountEl.textContent = formatCurrency(targetAmount, targetCurrency);
                if (exchangeRateEl && fxRate)
                    exchangeRateEl.textContent = `1 ${sourceCurrency} = ${fxRate} ${targetCurrency}`;
            }
        }

        if (params.value.disable_inputs === true) {
            bookingSummary.disableInputs = true;
        }
    };

    // --- SDK Functions ---
    const loadHotelpayScript = async () => {
        const existingScript = document.getElementById('hotelpay-sdk-script');
        if (existingScript) document.head.removeChild(existingScript);
        const sdkUrl = config.sdkUrl;

        logger.info(`Loading ${getCurrentEnvironment()} SDK from:`, sdkUrl);

        return new Promise<void>((resolve, reject) => {
            const script = document.createElement('script');
            script.id = 'hotelpay-sdk-script';
            script.src = sdkUrl;
            script.async = true;
            script.onerror = () => reject(new Error(`Failed to load SDK from ${sdkUrl}`));
            script.onload = () => {
                logger.info('SDK script loaded successfully');
                resolve();
            };
            document.head.appendChild(script);
        });
    };

    const loadSDK = async (maxRetries = 3, retryDelay = 1000) => {
        for (let i = 0; i <= maxRetries; i++) {
            try {
                if (window.Hotelpay && window.Hotelpay.Checkout) {
                    checkoutInstance = window.Hotelpay.Checkout;
                    let sdkMode;
                    switch (config.sdkMode) {
                        case 'development':
                            sdkMode = window.Hotelpay.Mode.Development;
                            break;
                        case 'testing':
                            sdkMode = window.Hotelpay.Mode.Testing;
                            break;
                        case 'production':
                            sdkMode = window.Hotelpay.Mode.Production;
                            break;
                        default:
                            sdkMode = window.Hotelpay.Mode.Development;
                    }
                    checkoutInstance.setMode(sdkMode);

                    if (hotelConfig.value?.apiKey) {
                        checkoutInstance.setApiKey(hotelConfig.value.apiKey);
                    } else {
                        throw new Error('API Key is missing');
                    }

                    if (params.value?.currency) {
                        checkoutInstance.setCurrency(params.value.currency as 'MXN' | 'USD');
                        bookingSummary.totalCurrency = params.value.currency;
                    }

                    hasCurrencyConversion.value = !!(
                        params.value?.source_currency &&
                        params.value?.fx_rate &&
                        params.value?.source_currency !== params.value?.currency
                    );

                    const computedTotal = computeChargeAmount(params.value ?? null);
                    if (computedTotal !== null) {
                        checkoutInstance.setTotal(computedTotal);
                        bookingSummary.totalAmount = String(computedTotal);
                    } else if (params.value?.total !== undefined) {
                        const rawTotal = Number(params.value.total);
                        if (!Number.isNaN(rawTotal)) {
                            checkoutInstance.setTotal(rawTotal);
                            bookingSummary.totalAmount = String(rawTotal);
                        }
                    }

                    await updatePaymentSummary();

                    if (params.value?.checkin) {
                        if (params.value.checkin.includes('T') || params.value.checkin.includes('Z')) {
                            const checkinDate = parseISO(params.value.checkin);
                            bookingSummary.checkIn = checkinDate.toISOString().split('T')[0];
                        } else {
                            bookingSummary.checkIn = params.value.checkin;
                        }
                    }
                    if (params.value?.checkout) {
                        if (params.value.checkout.includes('T') || params.value.checkout.includes('Z')) {
                            const checkoutDate = parseISO(params.value.checkout);
                            bookingSummary.checkOut = checkoutDate.toISOString().split('T')[0];
                        } else {
                            bookingSummary.checkOut = params.value.checkout;
                        }
                    }
                    if (params.value?.rooms)
                        bookingSummary.rooms = Number(params.value.rooms) || 1;
                    if (params.value?.guests)
                        bookingSummary.guests = Number(params.value.guests) || 1;
                    if (params.value?.booking_number)
                        bookingSummary.bookingNumber = params.value.booking_number;
                    if (
                        params.value?.items &&
                        Array.isArray(params.value.items) &&
                        params.value.items.length > 0
                    ) {
                        bookingSummary.items = params.value.items;
                    }
                    if (params.value?.disable_inputs !== undefined)
                        bookingSummary.disableInputs = Boolean(params.value.disable_inputs);
                    if (params.value?.url_failure) bookingSummary.urlFailure = params.value.url_failure;

                    checkoutInstance.setLanguage(language.value);

                    if (params.value?.installments_config) {
                        try {
                            checkoutInstance.setInstallmentsConfig(params.value.installments_config);
                        } catch (error) {
                            logger.error('Error setting installments config:', error);
                        }
                    }

                    checkoutInstance.on('sdk-ready', () => {
                        logger.info('SDK ready event received - form is loaded');
                        isSdkReady.value = true;
                    });
                    checkoutInstance.on(
                        'complete-form-card',
                        (data: { is_valid?: boolean; valid?: boolean }) => {
                            isCardFormValid.value = data.is_valid ?? data.valid ?? false;
                        }
                    );
                    checkoutInstance.on('transaction-start', () => {
                        paymentStatus.value = 'processing';
                        showProcessingModal(t('processingPayment'));
                    });
                    checkoutInstance.on('transaction-completed', (data: any) => {
                        isCardFormValid.value = false;
                        logger.info(
                            '[DEBUG] transaction-completed event received:',
                            JSON.stringify(data, null, 2)
                        );
                        confirmationCode.value = data.confirmationCode || data.shortUuid || '';
                        const now = new Date();
                        const locale =
                            language.value === 'es'
                                ? 'es-ES'
                                : language.value === 'en'
                                ? 'en-US'
                                : language.value === 'fr'
                                ? 'fr-FR'
                                : 'pt-BR';
                        const dateTimeOptions: Intl.DateTimeFormatOptions = {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                            hour12: false,
                            timeZoneName: 'short',
                        };
                        paymentTimestamp.value = now.toLocaleString(locale, dateTimeOptions);
                        closeProcessingModal();
                        showPaymentSuccess();
                    });
                    checkoutInstance.on('paymentCompleted', (data: any) => {
                        isCardFormValid.value = false;
                        closeProcessingModal();
                        showPaymentSuccess();
                    });
                    checkoutInstance.on('transaction-failed', (error: any) => {
                        const errorData = error?.data || error?.details?.response?.data || error;
                        const errorCode = errorData?.code;
                        const errorMessage = getFriendlyErrorMessage(
                            errorCode,
                            errorData?.message,
                            language.value
                        );
                        const timestamp = errorData?.timestamp;
                        closeProcessingModal();
                        showPaymentErrorModal(errorMessage, errorCode, timestamp);
                    });
                    checkoutInstance.on('msi', (data: any) => {
                        logger.info('MSI event:', data);
                    });
                    checkoutInstance.on('require-installments', (data: any) => {
                        logger.info('Installments required:', data);
                        closeProcessingModal();
                    });
                    checkoutInstance.on('3ds-start', () => {
                        showProcessingModal(t('authenticating3DS'));
                    });
                    checkoutInstance.on('3ds-end', () => {
                        closeProcessingModal();
                    });
                    checkoutInstance.on('error', (error: any) => {
                        const errorData = error?.data || error?.details?.response?.data || error;
                        const errorCode = errorData?.code;
                        const errorMessage = getFriendlyErrorMessage(
                            errorCode,
                            errorData?.message,
                            language.value
                        );
                        const timestamp = errorData?.timestamp;
                        closeProcessingModal();
                        showPaymentErrorModal(errorMessage, errorCode, timestamp);
                    });
                    checkoutInstance.on('transaction-error', (error: any) => {
                        const errorData = error?.data || error?.details?.response?.data || error;
                        const errorCode = errorData?.code;
                        const errorMessage = getFriendlyErrorMessage(
                            errorCode,
                            errorData?.message,
                            language.value
                        );
                        const timestamp = errorData?.timestamp;
                        closeProcessingModal();
                        showPaymentErrorModal(errorMessage, errorCode, timestamp);
                    });

                    // Wait for the container to exist in the DOM before rendering
                    logger.info('Waiting for checkout container to be available in DOM...');
                    await waitForElement('#hotelpay-checkout-container', 15000);
                    logger.info('Checkout container found, rendering iframe...');

                    await checkoutInstance.renderIframe('#hotelpay-checkout-container');
                    isSdkReady.value = true;
                    return;
                }
            } catch (error) {
                logger.error(`Error loading SDK (attempt ${i + 1}):`, error);
                if (i === maxRetries) {
                    paymentStatus.value = 'error';
                    showPaymentErrorModal('Failed to load payment SDK.');
                    return;
                }
                await new Promise((res) => setTimeout(res, retryDelay * (i + 1)));
            }
        }
    };

    // --- Form Submit ---
    function handleFormSubmit(): void {
        if (!checkoutInstance) return;

        if (!checkOnlineStatus()) {
            return;
        }

        if (!canProceedWithPayment.value) {
            if (hasPrivacyOrTermsUrls.value && !isPoliciesAccepted.value) {
                showPaymentErrorModal(t('mustAcceptPolicies'));
            }
            return;
        }

        const addressForSDK = {
            ...billingAddress,
            country_code: billingAddress.country,
            address2: '',
        };
        checkoutInstance.setAddress(addressForSDK);

        let final_description: string | undefined = params.value?.description;
        if (!final_description && hotelConfig.value?.name) {
            let desc_parts = [`Pago para ${hotelConfig.value.name}`];
            if (params.value?.concept) desc_parts.push(`[${params.value.concept}]`);
            if (params.value?.booking_number)
                desc_parts.push(`[reserva ${params.value.booking_number}]`);
            if (params.value?.referenceId) desc_parts.push(`ref ${params.value.referenceId}`);
            final_description = desc_parts.join(', ');
            if (final_description.length > 200)
                final_description = final_description.substring(0, 197) + '...';
        }

        let final_items: any[] | undefined = params.value?.items;
        if (!final_items || final_items.length === 0) {
            const item_description =
                final_description || `Pago para ${hotelConfig.value?.name || 'compra'}`;
            final_items = [
                {
                    description: item_description,
                    price: Number(bookingSummary.totalAmount) || 0,
                    quantity: 1,
                },
            ];
        }

        const detailsPayload: SdkDetailsPayload = {
            customer_info: customerInfo,
            customer_address: addressForSDK,
            referenceId: params.value?.referenceId,
            description: final_description,
            items: final_items,
        };

        const reserveDataCandidate = {
            ...params.value?.reserve_data,
            booking_number: params.value?.booking_number,
            checkin: bookingSummary.checkIn,
            checkout: bookingSummary.checkOut,
            guests_adults: bookingSummary.guests,
            guests_children: null,
        };

        const hasBookingNumber =
            reserveDataCandidate.booking_number &&
            typeof reserveDataCandidate.booking_number === 'string' &&
            reserveDataCandidate.booking_number.trim() !== '';

        const isRealDate = (dateValue: any): boolean => {
            return (
                dateValue &&
                dateValue !== '' &&
                typeof dateValue === 'string' &&
                !isNaN(Date.parse(dateValue))
            );
        };

        const checkinValid = isRealDate(reserveDataCandidate.checkin);
        const checkoutValid = isRealDate(reserveDataCandidate.checkout);
        const hasValidDates = checkinValid && checkoutValid;

        if (hasBookingNumber || hasValidDates) {
            detailsPayload.reserve_data = reserveDataCandidate;
        }

        if (requiresInvoice.value) {
            detailsPayload.requires_invoice = true;
            detailsPayload.invoice_data = {
                ...invoiceData,
                email: invoiceData.email || customerInfo.email,
                pais: billingAddress.country,
            };
        }

        checkoutInstance.setDetails(detailsPayload);
        checkoutInstance.processPayment().catch((error: any) => {
            logger.error('Critical error calling processPayment:', error);
            const errorData = error?.data || error?.response?.data || error;
            const errorCode = errorData?.code || error?.code || 'HP-SYS-999';
            const errorMessage = errorData?.message || error?.message;
            const friendlyMessage = getFriendlyErrorMessage(
                errorCode,
                errorMessage,
                language.value
            );
            closeProcessingModal();
            showPaymentErrorModal(friendlyMessage, errorCode, errorData?.timestamp);
            paymentStatus.value = 'error';
        });
    }

    // --- Initialization ---
    const initialize = async (config: HotelConfig) => {
        hotelConfig.value = config;
        bookingSummary.hotelName = config.name || '';
        bookingSummary.hotelAddress = config.address || '';
        updatePageTitle();

        // Aplicar colores del hotel
        if (config.bgColor) {
            document.documentElement.style.setProperty('--hotel-bg-color', config.bgColor);
        }
        if (config.fgColor) {
            document.documentElement.style.setProperty('--hotel-fg-color', config.fgColor);
        }

        // Cargar idioma guardado
        const savedLanguage = localStorage.getItem('selectedLanguage');
        if (savedLanguage && ['en', 'es', 'fr', 'pt'].includes(savedLanguage)) {
            language.value = savedLanguage as Language;
        } else {
            const browserLang = navigator.language.split('-')[0].toLowerCase();
            if (['en', 'es', 'fr', 'pt'].includes(browserLang))
                language.value = browserLang as Language;
        }

        // Prefill customer info from params if available
        if (params.value?.first_name) customerInfo.first_name = params.value.first_name;
        if (params.value?.last_name) customerInfo.last_name = params.value.last_name;
        if (params.value?.email) customerInfo.email = params.value.email;
        if (params.value?.phone) customerInfo.phone = params.value.phone;

        await updatePaymentSummary();

        try {
            await loadHotelpayScript();
            await loadSDK();
        } catch (error) {
            logger.error('Failed to load Hotelpay SDK script:', error);
            paymentStatus.value = 'error';
        }
    };

    return {
        // State
        paymentStatus,
        isCardFormValid,
        isPoliciesAccepted,
        hotelConfig,
        hasCurrencyConversion,
        language,
        requiresInvoice,
        isSdkReady,
        confirmationCode,
        paymentTimestamp,
        copied,
        params,
        pageMode,
        isLanguageMenuOpen,
        rfcError,
        billingCountryDisplay,
        bookingSummary,
        customerInfo,
        billingAddress,
        invoiceData,

        // Computed
        contactInfo,
        privacyUrl,
        termsUrl,
        hasPrivacyOrTermsUrls,
        canProceedWithPayment,
        countries,

        // Methods
        t,
        getLocalizedContent,
        formatCurrency,
        formatDualCurrency,
        formatDate,
        convertCurrency,
        setLanguage,
        toggleInvoiceForm,
        validateRFC,
        handleRfcValidation,
        transformToUppercase,
        showPaymentErrorModal,
        showPaymentSuccess,
        showProcessingModal,
        closeProcessingModal,
        copyConfirmationCode,
        checkOnlineStatus,
        handleFormSubmit,
        initialize,
        updatePaymentSummary,
        updatePageTitle,
        loadHotelpayScript,
        loadSDK,
    };
}
